// src/components/Checkout/GuestDetailsForm.jsx
import React from "react";
import DeliveryAddressForm from "./DeliveryAddressForm";
import ErrorModal from "../ErrorModal"; // ✅ adjust path if needed

export default function GuestDetailsForm({
  serviceType,
  isDineIn,
  setIsDineIn,
  fullName,
  setFullName,
  email,
  setEmail,
  phone,
  setPhone,
  tableNumber,
  setTableNumber,
  notes,
  setNotes,
  deliveryAddress,
  setDeliveryAddress,
  deliveryQuote,
  updateDeliveryField,

  // ✅ Pickup
  pickupDate,
  setPickupDate,
  pickupTime,
  setPickupTime,
}) {
  const Required = () => <span className="text-red-500 ml-1">*</span>;

  // ✅ Error modal state
  const [errorMessage, setErrorMessage] = React.useState("");

  // ✅ Generate time slots
  const generateTimeSlots = () => {
    const slots = [];
    let startHour = 14;
    let endHour = 21;

    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push(`${String(hour).padStart(2, "0")}:00`);
      if (hour !== endHour) {
        slots.push(`${String(hour).padStart(2, "0")}:30`);
      }
    }
    
    return slots.filter(time => {
      if (isDineIn) {
        return time >= "14:30";
      } else {
        return time >= "15:00";
      }
    });
  };

  const timeSlots = generateTimeSlots();

  // ✅ Format time (AM/PM)
  const formatTime = (time) => {
    const [hour, minute] = time.split(":");
    let h = parseInt(hour);
    const ampm = h >= 12 ? "PM" : "AM";

    if (h > 12) h -= 12;
    if (h === 0) h = 12;

    return `${h}:${minute} ${ampm}`;
  };

  // ✅ Only allow Wed–Sun
  // const isValidPickupDay = (dateString) => {
  //   const day = new Date(dateString).getDay();
  //   return day === 0 || (day >= 3 && day <= 6);
  // };

  const handlePickupDateChange = (value) => {
    // if (!isValidPickupDay(value)) {
    //   setErrorMessage("Pickup is only available Wednesday to Sunday.");
    //   return;
    // }
    setPickupDate(value);
  };

  // ✅ Prevent past times (today)
  const isToday = (date) => {
    const today = new Date().toISOString().split("T")[0];
    return date === today;
  };

  const isPastTime = (time) => {
    if (!pickupDate || !isToday(pickupDate)) return false;

    const now = new Date();
    const [hour, minute] = time.split(":");

    const slotTime = new Date();
    slotTime.setHours(hour, minute, 0);

    return slotTime < now;
  };

  return (
    <>
      <section className="bg-white dark:bg-slate-900/50 p-6 lg:p-8 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800">
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-slate-900 dark:text-white">
          <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm">
            2
          </span>
          Your Details
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Full Name */}
          <div className="space-y-2">
            <label className="text-sm font-semibold">
              Full Name <Required />
            </label>
            <input
              required
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                localStorage.setItem("guest_fullName", e.target.value);
              }}
              className="w-full px-4 py-3 rounded-lg border"
              type="text"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm font-semibold">
              Email <Required />
            </label>
            <input
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                localStorage.setItem("guest_email", e.target.value);
              }}
              className="w-full px-4 py-3 rounded-lg border"
              type="email"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <label className="text-sm font-semibold">
              WhatsApp Number <Required />
            </label>
            <input
              required
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                localStorage.setItem("guest_phone", e.target.value);
              }}
              className="w-full px-4 py-3 rounded-lg border"
              type="tel"
            />
          </div>

          {/* Delivery */}
          {serviceType === "delivery" && (
            <DeliveryAddressForm
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              deliveryQuote={deliveryQuote}
              updateDeliveryField={updateDeliveryField}
            />
          )}

          {/* ✅ Pickup & Eat In */}
          {serviceType === "pick_up" && (
            <>
              {/* Takeaway / Dine In Toggle */}
              <div className="space-y-2 lg:col-span-2">
                <label className="text-sm font-semibold">
                  How would you like to have your order? <Required />
                </label>
                <div className="flex gap-6 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="collection_method"
                      className="w-4 h-4 text-primary"
                      checked={!isDineIn}
                      onChange={() => setIsDineIn(false)}
                    />
                    <span>I will Pick Up</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="collection_method"
                      className="w-4 h-4 text-primary"
                      checked={isDineIn}
                      onChange={() => setIsDineIn(true)}
                    />
                    <span>I will Eat In</span>
                  </label>
                </div>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <label className="text-sm font-semibold">
                  {isDineIn ? "Dine-In Date" : "Pick up Date"} <Required />
                </label>
                <input
                  type="date"
                  required
                  value={pickupDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => handlePickupDateChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border"
                />
                {/* <p className="text-xs text-gray-500">
                  Wednesday – Sunday only
                </p> */}
              </div>

              {/* Time Slots */}
              <div className="space-y-2 lg:col-span-2">
                <label className="text-sm font-semibold">
                  {isDineIn ? "Dine-In Time" : "Pick up Time"} <Required />
                </label>

                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {timeSlots.map((time) => {
                    const disabled = isPastTime(time);

                    return (
                      <button
                        type="button"
                        key={time}
                        disabled={disabled}
                        onClick={() => setPickupTime(time)}
                        className={`px-3 py-2 rounded-lg text-sm border transition
                          ${
                            disabled
                              ? "opacity-40 cursor-not-allowed"
                              : pickupTime === time
                              ? "bg-primary text-white border-primary"
                              : "bg-white border-gray-200 hover:border-primary"
                          }
                        `}
                      >
                        {formatTime(time)}
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs text-gray-500">
                  {isDineIn ? "Available between 2:30 PM – 9:00 PM" : "Available between 3:00 PM – 9:00 PM"}
                </p>
              </div>
            </>
          )}

          {/* Notes */}
          <div className="space-y-2 lg:col-span-2">
            <label className="text-sm font-semibold">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-lg border"
            />
          </div>
        </div>
      </section>

      {/* ✅ Error Modal */}
      {errorMessage && (
        <ErrorModal
          message={errorMessage}
          onClose={() => setErrorMessage("")}
          title="Invalid Selection"
        />
      )}
    </>
  );
}